import { create } from 'zustand';
import type { Employee, EmployeeListParameters, EmployeeListResponse } from '@/types/employee';
import { getEmployeeDeletedList, getEmployeeList } from '@/api/employee';

type EmployeeListType = 'default' | 'delete';

interface EmployeeState {
  employees: Employee[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  isLoading: boolean;
  error: string | null;
  listType: EmployeeListType;
  
  // 검색 및 필터 상태
  searchParams: EmployeeListParameters;
  
  // Actions
  fetchEmployees: (params?: Partial<EmployeeListParameters>) => Promise<void>;
  setListType: (listType: EmployeeListType) => void;
  setSearchParams: (params: Partial<EmployeeListParameters>) => void;
  resetSearchParams: () => void;
  clearError: () => void;
}

// 기본 검색 파라미터
const defaultSearchParams: EmployeeListParameters = {
  shopNo: 1,
  page: 1,
  limit: 50,
  export: 'F',
};

export const useEmployeeStore = create<EmployeeState>((set, get) => ({
  employees: [],
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  limit: 50,
  isLoading: false,
  error: null,
  listType: 'default',
  searchParams: defaultSearchParams,

  fetchEmployees: async (params?: Partial<EmployeeListParameters>) => {
    set({ isLoading: true, error: null });
    
    try {
      // 새로 들어온 params를 그대로 사용 (기본값과만 병합)
      const finalParams = { ...defaultSearchParams, ...params };
      const { listType } = get();
      const fetcher = listType === 'delete' ? getEmployeeDeletedList : getEmployeeList;
      const response = await fetcher(finalParams);
      
      if (response.code === 200 && response.data) {
        const employeeListResponse: EmployeeListResponse = response.data;
        set({ 
          employees: employeeListResponse.employees,
          totalCount: employeeListResponse.total,
          currentPage: employeeListResponse.page,
          totalPages: employeeListResponse.totalPages,
          limit: employeeListResponse.limit,
          isLoading: false,
          error: null,
          searchParams: finalParams
        });
      } else {
        set({ 
          isLoading: false, 
          error: response.message || '직원 목록을 가져오는데 실패했습니다.' 
        });
      }
    } catch (error) {
      console.error('직원 목록 조회 오류:', error);
      let errorMessage = '직원 목록을 가져오는 중 오류가 발생했습니다.';
      if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
      }
      set({ isLoading: false, error: errorMessage });
    }
  },

  setListType: (listType: EmployeeListType) => {
    set({ listType });
  },

  setSearchParams: (params: Partial<EmployeeListParameters>) => {
    set(state => ({
      searchParams: { ...state.searchParams, ...params }
    }));
  },

  resetSearchParams: () => {
    set({ searchParams: defaultSearchParams });
  },

  clearError: () => {
    set({ error: null });
  },
}));
